import React, { useState, useEffect, useCallback } from 'react'
import { profileApi } from '../../services/profile'
import type { DealerProfile } from '../../services/profile'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
]

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (UTC-05:00)' },
  { value: 'America/Chicago', label: 'Central (UTC-06:00)' },
  { value: 'America/Denver', label: 'Mountain (UTC-07:00)' },
  { value: 'America/Phoenix', label: 'Arizona (UTC-07:00)' },
  { value: 'America/Los_Angeles', label: 'Pacific (UTC-08:00)' },
  { value: 'America/Anchorage', label: 'Alaska (UTC-09:00)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10:00)' },
]

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}

const EMPTY_PROFILE: DealerProfile = {
  name: '', email: '', address: '', address2: '', city: '', state: '', zip: '',
  timezone: 'America/Chicago', phone: '', fax: '', contact_email: '',
  first_name: '', last_name: '', url: '', gallery_url: '', google_place_id: '',
  social_fb_link: '', social_tw_link: '', social_li_link: '',
  bg_complaint_contact: '', business_hours: {},
}

export function DealerProfileForm(): React.ReactElement {
  const [profile, setProfile] = useState<DealerProfile>(EMPTY_PROFILE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [dirty, setDirty] = useState(false)

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await profileApi.getProfile()
      setProfile({ ...EMPTY_PROFILE, ...data })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const updateField = (field: keyof DealerProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }))
    setDirty(true)
    setSuccess(false)
  }

  const updateHours = (day: string, field: 'open' | 'close', value: string) => {
    setProfile(prev => {
      const hours = { ...prev.business_hours }
      const existing = hours[day]
      hours[day] = {
        open: field === 'open' ? value : (existing?.open || '09:00'),
        close: field === 'close' ? value : (existing?.close || '17:00'),
      }
      return { ...prev, business_hours: hours }
    })
    setDirty(true)
    setSuccess(false)
  }

  const toggleDayClosed = (day: string) => {
    setProfile(prev => {
      const hours = { ...prev.business_hours }
      if (hours[day] === null || hours[day] === undefined) {
        hours[day] = { open: '09:00', close: '17:00' }
      } else {
        hours[day] = null
      }
      return { ...prev, business_hours: hours }
    })
    setDirty(true)
    setSuccess(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)
      const updated = await profileApi.updateProfile(profile)
      setProfile({ ...EMPTY_PROFILE, ...updated })
      setDirty(false)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 4000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-secondary">Dealer Profile</h1>
        <button
          type="submit"
          disabled={saving || !dirty}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            dirty
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Status messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Profile saved successfully.
        </div>
      )}

      <div className="space-y-6">
        {/* Dealership Information */}
        <Section title="Dealership Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField label="Dealership Name" value={profile.name} onChange={v => updateField('name', v)} required />
            <InputField label="Email" value={profile.email} onChange={v => updateField('email', v)} type="email" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <InputField label="Phone" value={profile.phone} onChange={v => updateField('phone', v)} type="tel" />
            <InputField label="Fax" value={profile.fax} onChange={v => updateField('fax', v)} type="tel" />
          </div>
          <div className="mt-4">
            <InputField label="Address" value={profile.address} onChange={v => updateField('address', v)} />
          </div>
          <div className="mt-4">
            <InputField label="Address Line 2" value={profile.address2} onChange={v => updateField('address2', v)} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <InputField label="City" value={profile.city} onChange={v => updateField('city', v)} />
            <SelectField label="State" value={profile.state} onChange={v => updateField('state', v)} options={US_STATES.map(s => ({ value: s, label: s }))} placeholder="Select..." />
            <InputField label="ZIP" value={profile.zip} onChange={v => updateField('zip', v)} />
            <SelectField label="Timezone" value={profile.timezone} onChange={v => updateField('timezone', v)} options={TIMEZONES} />
          </div>
        </Section>

        {/* Contact Person */}
        <Section title="Contact Person">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InputField label="First Name" value={profile.first_name} onChange={v => updateField('first_name', v)} />
            <InputField label="Last Name" value={profile.last_name} onChange={v => updateField('last_name', v)} />
            <InputField label="Contact Email" value={profile.contact_email} onChange={v => updateField('contact_email', v)} type="email" />
          </div>
        </Section>

        {/* Website */}
        <Section title="Website & Links">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField label="Website URL" value={profile.url} onChange={v => updateField('url', v)} type="url" placeholder="https://..." />
            <InputField label="Gallery URL" value={profile.gallery_url} onChange={v => updateField('gallery_url', v)} type="url" placeholder="https://..." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <InputField label="Google Place ID" value={profile.google_place_id} onChange={v => updateField('google_place_id', v)} />
            <InputField label="Buyer's Guide Complaint Contact" value={profile.bg_complaint_contact} onChange={v => updateField('bg_complaint_contact', v)} />
          </div>
        </Section>

        {/* Social Links */}
        <Section title="Social Media">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InputField label="Facebook" value={profile.social_fb_link} onChange={v => updateField('social_fb_link', v)} type="url" placeholder="https://facebook.com/..." />
            <InputField label="Twitter / X" value={profile.social_tw_link} onChange={v => updateField('social_tw_link', v)} type="url" placeholder="https://twitter.com/..." />
            <InputField label="LinkedIn" value={profile.social_li_link} onChange={v => updateField('social_li_link', v)} type="url" placeholder="https://linkedin.com/..." />
          </div>
        </Section>

        {/* Business Hours */}
        <Section title="Business Hours">
          <div className="space-y-3">
            {DAYS_OF_WEEK.map(day => {
              const hours = profile.business_hours[day]
              const isOpen = hours !== null && hours !== undefined

              return (
                <div key={day} className="flex items-center gap-4">
                  <div className="w-24 sm:w-28 flex-shrink-0">
                    <span className="text-sm font-medium text-secondary">{DAY_LABELS[day]}</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isOpen}
                      onChange={() => toggleDayClosed(day)}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-grey">{isOpen ? 'Open' : 'Closed'}</span>
                  </label>
                  {isOpen && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={hours?.open || '09:00'}
                        onChange={e => updateHours(day, 'open', e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                      <span className="text-sm text-grey">to</span>
                      <input
                        type="time"
                        value={hours?.close || '17:00'}
                        onChange={e => updateHours(day, 'close', e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Section>
      </div>

      {/* Bottom save button */}
      <div className="mt-8 flex justify-end">
        <button
          type="submit"
          disabled={saving || !dirty}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            dirty
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

// Reusable sub-components

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
      <h2 className="text-base font-semibold text-secondary mb-4">{title}</h2>
      {children}
    </div>
  )
}

function InputField({
  label, value, onChange, type = 'text', required = false, placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-grey mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-secondary placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
      />
    </div>
  )
}

function SelectField({
  label, value, onChange, options, placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-grey mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-secondary focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors bg-white"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}
